import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, FunnelSimple, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = searchParams.get("category") || "all";
  const search = searchParams.get("search") || "";
  const sort = searchParams.get("sort") || "featured";
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    api.get("/products/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const params = { sort };
    if (category !== "all") params.category = category;
    if (search) params.search = search;
    api
      .get("/products", { params })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, search, sort]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all" && value !== "") next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    updateParam("search", searchInput.trim());
  };

  return (
    <div data-testid="shop-page" className="mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:px-12">
      <div className="border-b border-cyan/15 pb-8">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-cyan">// Catalog</span>
        <h1 className="mt-3 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl lg:text-5xl">
          Research Peptide Catalog
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-teal-muted">
          All compounds supplied for laboratory research use only. Browse {products.length}{" "}
          available {products.length === 1 ? "compound" : "compounds"}.
        </p>
      </div>

      <div className="grid gap-10 py-10 lg:grid-cols-[240px_1fr]">
        {/* Sidebar filters */}
        <aside className="space-y-8">
          <form onSubmit={onSearchSubmit}>
            <label className="mb-3 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-cyan">
              <MagnifyingGlass size={14} /> Search
            </label>
            <input
              data-testid="shop-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="e.g. BPC-157"
              className="w-full border border-cyan/15 bg-navy-surface px-3 py-2.5 font-mono text-sm text-white placeholder:text-teal/50 outline-none transition-colors focus:border-cyan/50"
            />
          </form>

          <div>
            <span className="mb-3 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-cyan">
              <FunnelSimple size={14} /> Category
            </span>
            <div className="flex flex-col gap-1">
              <button
                data-testid="category-filter-all"
                onClick={() => updateParam("category", "all")}
                className={`border-l-2 px-3 py-2 text-left font-mono text-xs uppercase tracking-wider transition-all ${
                  category === "all"
                    ? "border-cyan bg-cyan/10 text-cyan"
                    : "border-transparent text-teal-muted hover:border-cyan/40 hover:text-white"
                }`}
              >
                All Products
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`category-filter-${c}`}
                  onClick={() => updateParam("category", c)}
                  className={`border-l-2 px-3 py-2 text-left font-mono text-xs uppercase tracking-wider transition-all ${
                    category === c
                      ? "border-cyan bg-cyan/10 text-cyan"
                      : "border-transparent text-teal-muted hover:border-cyan/40 hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Products */}
        <div>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {category !== "all" && (
                <button
                  onClick={() => updateParam("category", "all")}
                  className="flex items-center gap-1.5 border border-cyan/30 bg-cyan/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-cyan"
                >
                  {category} <X size={12} />
                </button>
              )}
              {search && (
                <button
                  onClick={() => updateParam("search", "")}
                  className="flex items-center gap-1.5 border border-cyan/30 bg-cyan/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-cyan"
                >
                  “{search}” <X size={12} />
                </button>
              )}
            </div>
            <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
              <SelectTrigger
                data-testid="sort-select"
                className="w-[200px] border-cyan/20 bg-navy-surface font-mono text-xs uppercase tracking-wider text-white"
              >
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent className="border-cyan/20 bg-navy-surface font-mono text-xs text-white">
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="name">Name: A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading && (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`shop-skeleton-${i}`} className="aspect-[3/4] animate-pulse border border-cyan/10 bg-navy-surface" />
              ))}
            </div>
          )}
          {!loading && products.length === 0 && (
            <div className="border border-cyan/15 bg-navy-surface p-16 text-center" data-testid="no-products">
              <p className="font-mono text-sm uppercase tracking-wider text-teal-muted">
                No compounds match your filters
              </p>
            </div>
          )}
          {!loading && products.length > 0 && (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
